import React from 'react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/bg';
import {
  DAY_NAMES,
  MUSCLE_GROUPS,
  BROAD_MUSCLE_GROUPS,
  MUSCLE_GROUP_COLORS,
  type ExerciseOption,
} from './types';
import { TRAINING_LABEL_OPTIONS, type TrainingLabel } from '../../services/types';
import type { TrainingProgram } from '../../services/types';

interface ProgressFiltersProps {
  selectedProgramId: number | null;
  selectedExerciseId: number | null;
  selectedVariation: number | null;
  selectedMuscleGroup: string | null;
  selectedTrainingLabel: TrainingLabel | null;
  selectedProgramDay: number | null;
  selectedGymId: number | null;
  programs: TrainingProgram[];
  gyms: Array<{ id: number; name: string }>;
  availableProgramDays: number[];
  dateFrom: string;
  dateTo: string;
  groups: Array<{ id: number; name: string }>;
  selectedGroupId: number | null;
  groupedExerciseOptions: { exercise: ExerciseOption; isVariation: boolean }[];
  variationOptions: ExerciseOption[];
  onGroupChange: (groupId: number | null) => void;
  onManageGroups: () => void;
  onProgramChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onExerciseChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onVariationChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onMuscleGroupChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onTrainingLabelChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onProgramDayChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onGymChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onDateChange: (from: string, to: string) => void;
}

const selectStyle = (isActive: boolean, borderColor = '#007bff'): React.CSSProperties => ({
  padding: '10px 8px',
  borderRadius: '8px',
  border: isActive ? `2px solid ${borderColor}` : '1px solid #555',
  fontSize: '14px',
  width: '100%',
  backgroundColor: '#1e1e1e',
  color: 'inherit',
});

const ProgressFilters: React.FC<ProgressFiltersProps> = ({
  selectedProgramId,
  selectedExerciseId, selectedVariation, selectedMuscleGroup, selectedTrainingLabel, selectedProgramDay, selectedGymId, gyms,
  programs,
  availableProgramDays, dateFrom, dateTo,
  groups, selectedGroupId,
  groupedExerciseOptions, variationOptions,
  onGroupChange, onManageGroups,
  onProgramChange, onExerciseChange, onVariationChange, onMuscleGroupChange, onTrainingLabelChange, onProgramDayChange, onGymChange, onDateChange,
}) => {
  return (
    <Box className="progress-filters" sx={{ mb: 2, display: 'flex', gap: 1, flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' } }}>
        <select
          onChange={(e) => onGroupChange(e.target.value ? Number(e.target.value) : null)}
          value={selectedGroupId || ''}
          style={selectStyle(!!selectedGroupId, '#e91e63')}
        >
          <option value="">★ Групи упражнения</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={onManageGroups}
          style={{
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #e91e63',
            backgroundColor: '#1e1e1e',
            color: '#e91e63',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          Управление
        </button>

        <select onChange={onProgramChange} value={selectedProgramId || ''} style={selectStyle(!!selectedProgramId, '#1565c0')}>
          <option value="">Всички програми</option>
          {programs.map((program) => (
            <option key={program.id} value={program.id}>
              {program.active ? '● ' : ''}{program.name}
            </option>
          ))}
        </select>

        <select onChange={onExerciseChange} value={selectedExerciseId || ''} style={selectStyle(!!selectedExerciseId)}>
          <option value="">Всички упражнения</option>
          {groupedExerciseOptions.map(({ exercise, isVariation }) => (
            <option key={exercise.id} value={exercise.id}>
              {isVariation ? `  └ ${exercise.variationName || exercise.name}` : exercise.name} ({exercise.primaryMuscleGroup || exercise.muscleGroup})
            </option>
          ))}
        </select>

        {variationOptions.length > 0 && (
          <select onChange={onVariationChange} value={selectedVariation || ''} style={selectStyle(!!selectedVariation, '#9c27b0')}>
            <option value="">Всички вариации</option>
            {variationOptions.map(v => (
              <option key={v.id} value={v.id}>{v.variationName || v.name}</option>
            ))}
          </select>
        )}

        <select
          onChange={onMuscleGroupChange}
          value={selectedMuscleGroup || ''}
          style={selectStyle(
            !!selectedMuscleGroup,
            BROAD_MUSCLE_GROUPS.find(bg => bg.value === selectedMuscleGroup)?.color || MUSCLE_GROUP_COLORS[selectedMuscleGroup || ''] || '#007bff'
          )}
        >
          <option value="">Мускулна Група</option>
          <optgroup label="Цяла Група">
            {BROAD_MUSCLE_GROUPS.map(mg => (
              <option key={mg.value} value={mg.value}>{mg.label}</option>
            ))}
          </optgroup>
          <optgroup label="Отделна Мускулна Група">
            {MUSCLE_GROUPS.map(mg => (
              <option key={mg.value} value={mg.value}>{mg.label}</option>
            ))}
          </optgroup>
        </select>

        <select onChange={onTrainingLabelChange} value={selectedTrainingLabel || ''} style={selectStyle(!!selectedTrainingLabel, '#ff9800')}>
          <option value="">Label на тренировка</option>
          {TRAINING_LABEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <select onChange={onProgramDayChange} value={selectedProgramDay || ''} style={selectStyle(!!selectedProgramDay)}>
          <option value="">Всички дни</option>
          {availableProgramDays.map(day => (
            <option key={day} value={day}>
              {DAY_NAMES[day] || `День ${day}`}
            </option>
          ))}
        </select>

        {gyms.length > 0 && (
          <select onChange={onGymChange} value={selectedGymId || ''} style={selectStyle(!!selectedGymId, '#2e7d32')}>
            <option value="">Всички зали</option>
            {gyms.map((gym) => (
              <option key={gym.id} value={gym.id}>{gym.name}</option>
            ))}
          </select>
        )}
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="bg">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <DatePicker
            label="От"
            value={dayjs(dateFrom)}
            format="DD.MM.YYYY"
            onChange={(val: Dayjs | null) => { if (val?.isValid()) onDateChange(val.format('YYYY-MM-DD'), dateTo); }}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 140, maxWidth: 160 } } }}
          />
          <DatePicker
            label="До"
            value={dayjs(dateTo)}
            format="DD.MM.YYYY"
            onChange={(val: Dayjs | null) => { if (val?.isValid()) onDateChange(dateFrom, val.format('YYYY-MM-DD')); }}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 140, maxWidth: 160 } } }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default ProgressFilters;
